import React, { useMemo, useState } from "react";

import { Button, Popover, Stack, TextField, Typography } from "@mui/material";
import { NumberInput } from "components/forms";
import { BenchmarkLiquidityGraphParams } from "store/types/reports/benchmark";

import { AssetDistributionModal } from "./AssetDistributionModal";

interface Props {
  isFreemium?: boolean;
  initialValues: BenchmarkLiquidityGraphParams;
  onApply: (data: BenchmarkLiquidityGraphParams) => void;
}

const assetClasses = [
  { id: "buyout", label: "Buyout" },
  { id: "venture_capital", label: "Venture capital" },
  { id: "secondary", label: "Secondary" },
  { id: "debt", label: "Debt" }
];

export const MultipleAssetEditPopup: React.FC<Props> = ({ initialValues, onApply, isFreemium = false }) => {
  const [popupAnchor, setPopupAnchor] = useState<HTMLButtonElement | null>(null);
  const [values, setValues] = useState<BenchmarkLiquidityGraphParams>(initialValues);
  const [showWarning, setShowWarning] = useState(false);

  const totalPercentage = useMemo(() => {
    return Object.values(values).reduce((sum, value) => sum + value, 0);
  }, [values]);

  const handleChange = (id: keyof BenchmarkLiquidityGraphParams, value: number) => {
    setValues((prev) => ({
      ...prev,
      [id]: value
    }));
  };

  const handleApplyClick = () => {
    if (totalPercentage !== 100) {
      setShowWarning(true);
    } else {
      onApply(values);
      setPopupAnchor(null);
      setShowWarning(false);
    }
  };

  const handleConfirmUneven = () => {
    const totalSum = Object.values(values).reduce((acc, value) => acc + value, 0);
    if (totalSum === 0) return;

    const equalShare = 25;
    const proportionalValues = Object.keys(values).reduce(
      (acc, key) => ({
        ...acc,
        [key]: equalShare
      }),
      {} as BenchmarkLiquidityGraphParams
    );

    setValues(proportionalValues);
    setShowWarning(false);
    setPopupAnchor(null);
    onApply(proportionalValues);
  };

  return (
    <>
      {!isFreemium && (
        <Button onClick={(e) => setPopupAnchor(e.currentTarget)} sx={{ color: "#26A6AE" }}>
          Edit
        </Button>
      )}
      <Popover
        onClose={() => setPopupAnchor(null)}
        open={Boolean(popupAnchor)}
        anchorEl={popupAnchor}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
      >
        <Stack
          sx={{
            width: 520,
            bgcolor: "white",
            borderRadius: "8px",
            p: "24px",
            gap: "24px"
          }}
        >
          <Typography sx={{ fontSize: "18px", color: "#071F35" }}>Asset Class</Typography>

          <Stack spacing={2}>
            {assetClasses.map(({ id, label }) => (
              <Stack
                key={id}
                direction="row"
                alignItems="center"
                gap="16px"
                sx={{
                  p: "12px",
                  borderRadius: "8px"
                }}
              >
                <TextField label="Asset Class" value={label} disabled fullWidth />
                <NumberInput
                  size="small"
                  label="Share"
                  sx={{ width: 120 }}
                  value={values[id as keyof BenchmarkLiquidityGraphParams]}
                  onChange={(e) => handleChange(id as keyof BenchmarkLiquidityGraphParams, Number(e.target.value))}
                  numberInputProps={{
                    suffix: "%",
                    allowNegative: false,
                    isNumericString: true,
                    decimalScale: 2
                  }}
                />
              </Stack>
            ))}
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ color: "#496682", fontSize: "15px" }}>Total: {totalPercentage}%</Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={() => setPopupAnchor(null)}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleApplyClick}>
                Apply
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Popover>
      <AssetDistributionModal
        open={showWarning}
        onClose={() => setShowWarning(false)}
        onConfirm={handleConfirmUneven}
        isMore={totalPercentage > 100}
      />
    </>
  );
};
